// Librairies
import Axios from 'axios';
import AppSettings from '../../AppSettings';

// État réel de la connexion à l'API (navigator.onLine vaut souvent true avec une 4G inutilisable) :
// hors-ligne dès qu'un appel échoue sans réponse, en ligne à la première réponse, quelle qu'elle soit.
// Pendant une coupure, une sonde (parameters/ping) vérifie si l'API répond de nouveau, de plus en plus espacée.
export const PROBE_BACKOFF_MS = [15000, 30000, 60000, 120000, 300000];
export const PROBE_TIMEOUT_MS = 5000;
// Axios n'a pas de délai par défaut : une requête sans réponse après 10 s déclenche une sonde
export const SLOW_REQUEST_PROBE_MS = 10000;

const getNavigatorOnline = () => typeof navigator === 'undefined' || navigator.onLine;

// Fabrique : une instance neuve par test ; l'application utilise l'instance par défaut
export const createConnectivity = ({ backendUrl = AppSettings.getBackendUrl() } = {}) => {
    let online = getNavigatorOnline();
    let probeTimer = null;
    let probeAttempt = 0;
    // Sonde en cours : plusieurs échecs simultanés n'en lancent qu'une
    let probeInFlight = null;
    const listeners = new Set();
    const apiHost = (() => {
        try { return new URL(backendUrl).host; }
        catch { return null; }
    })();

    const scheduleProbe = () => {
        clearTimeout(probeTimer);
        probeTimer = setTimeout(async () => {
            probeTimer = null;
            await connectivity.probe();
            if (!online) scheduleProbe();
        }, PROBE_BACKOFF_MS[Math.min(probeAttempt++, PROBE_BACKOFF_MS.length - 1)]);
    };

    const setOnline = (value) => {
        if (value === online) return;
        online = value;
        if (online) {
            clearTimeout(probeTimer);
            probeTimer = null;
        } else {
            probeAttempt = 0;
            scheduleProbe();
        }
        listeners.forEach(listener => listener(online));
    };

    const connectivity = {
        isOnline: () => online,
        subscribe: (listener) => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        // Les sous-domaines de répartition (1. à 10.) comptent comme l'API
        isApiUrl: (url) => {
            if (!url || !apiHost) return false;
            try {
                const host = new URL(url, 'http://relative.invalid').host;
                return host === apiHost || host.endsWith('.' + apiHost);
            } catch { return false; }
        },
        reportSuccess: () => setOnline(true),
        reportNetworkFailure: () => setOnline(false),
        probe: () => probeInFlight || (probeInFlight = (async () => {
            if (!getNavigatorOnline()) {
                setOnline(false);
                return false;
            }
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
            try {
                await fetch(backendUrl + 'parameters/ping', { cache: 'no-store', signal: controller.signal });
                setOnline(true);
                return true;
            } catch {
                setOnline(false);
                return false;
            } finally {
                clearTimeout(timeout);
            }
        })().finally(() => { probeInFlight = null; }))
    };

    if (!online) scheduleProbe();
    return connectivity;
};

const connectivity = createConnectivity();
export default connectivity;
export const isOnline = () => connectivity.isOnline();

// Appelé une fois au démarrage (bootstrap.js). Une requête annulée volontairement ne compte pas comme une coupure.
export const installConnectivity = (axios = Axios, instance = connectivity) => {
    const slowTimers = new WeakMap();
    const clearSlowTimer = (config) => {
        if (config && slowTimers.has(config)) clearTimeout(slowTimers.get(config));
    };
    axios.interceptors.request.use(config => {
        if (instance.isApiUrl(config?.url) && instance.isOnline())
            slowTimers.set(config, setTimeout(() => instance.probe(), SLOW_REQUEST_PROBE_MS));
        return config;
    });
    axios.interceptors.response.use(response => {
        clearSlowTimer(response?.config);
        if (instance.isApiUrl(response?.config?.url)) instance.reportSuccess();
        return response;
    }, error => {
        clearSlowTimer(error?.config);
        if (!axios.isCancel(error) && instance.isApiUrl(error?.config?.url)) {
            if (error.response) instance.reportSuccess();
            // Échec sans réponse : vérifié par une sonde avant de passer hors-ligne (en prod, une 500 de l'API arrive sans
            // en-têtes CORS et ressemble à une coupure pour le navigateur)
            else instance.probe();
        }
        return Promise.reject(error);
    });
    window.addEventListener('online', () => instance.probe());
    window.addEventListener('offline', () => instance.reportNetworkFailure());
    // Sortie de veille : le réseau a pu revenir sans évènement « online »
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && !instance.isOnline()) instance.probe();
    });
};
